import TextInputIcon from "@redotech/redo-icons/text-input.svg";
import {
  type AiVerificationType,
  InputContentType,
  InputPartType,
  InputValidation,
  Step as ModelStep,
  TextValidationSettings,
} from "@redotech/redo-model/return-flow";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import {
  StepDownstream,
  StepId,
  StepType,
  StepTypeDetailsProps,
} from "../step";

export interface InputState {
  next: StepId | undefined;
  prompt: string;
  description: string;
  type: InputPartType | undefined;
  validation: InputValidation;
  exampleImage: URL | undefined;
  attempted?: boolean;
  customTitle?: string;
  minLength?: number;
  maxLength?: number;
  contentType?: InputContentType;
  customRegex?: string;
  customRegexErrorMessage?: string;
  useAdvancedValidation?: boolean;
  aiVerification?: AiVerificationType;
  aiVerificationStrictness?: number;
}
export const INPUT = (
  Details: React.ComponentType<StepTypeDetailsProps<InputState>>,
): StepType<InputState, ModelStep.Input> => ({
  Details: (props: StepTypeDetailsProps<InputState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Input",
  valid(state) {
    return (
      state.next !== undefined && !!state.prompt && state.type !== undefined
    );
  },
  Icon: TextInputIcon,
  description(state) {
    return state.prompt;
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  layout() {
    return BlockLayout.FULL;
  },
  empty: {
    prompt: "",
    description: "",
    type: InputPartType.TEXTAREA,
    validation: InputValidation.OPTIONAL,
    next: undefined,
    exampleImage: undefined,
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(`Invalid handle index ${handleIndex} for Input step`);
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  toModel(state, id) {
    const textValidationSettings: TextValidationSettings | undefined =
      state.type === InputPartType.TEXTAREA &&
      (state.minLength !== undefined ||
        state.maxLength !== undefined ||
        state.contentType !== undefined ||
        state.customRegex !== undefined ||
        state.useAdvancedValidation !== undefined)
        ? {
            minLength: state.minLength,
            maxLength: state.maxLength,
            contentType: state.contentType,
            customRegex: state.customRegex,
            customRegexErrorMessage: state.customRegexErrorMessage,
            useAdvancedValidation: state.useAdvancedValidation,
          }
        : undefined;

    return {
      customTitle: state.customTitle,
      type: ModelStep.INPUT,
      next: id(state.next!),
      parts: [
        {
          description: state.description,
          message: state.prompt,
          validation: state.validation,
          type: state.type!,
          exampleImage: state.exampleImage,
          textValidationSettings,
          aiVerification: state.aiVerification,
          aiVerificationStrictness: state.aiVerificationStrictness,
        },
      ],
    };
  },
  fromModel(model, id) {
    const settings = model.parts[0]?.textValidationSettings;
    return {
      customTitle: model.customTitle,
      next: id(model.next),
      prompt: model.parts[0]?.message || "",
      description: model.parts[0]?.description || "",
      validation: model.parts[0]?.validation || InputValidation.OPTIONAL,
      type: model.parts[0]?.type || InputPartType.TEXTAREA,
      exampleImage: (model.parts[0]?.exampleImage as URL) || undefined,
      minLength: settings?.minLength,
      maxLength: settings?.maxLength,
      contentType: settings?.contentType,
      customRegex: settings?.customRegex,
      customRegexErrorMessage: settings?.customRegexErrorMessage,
      useAdvancedValidation: settings?.useAdvancedValidation,
      aiVerification: model.parts[0]?.aiVerification,
      aiVerificationStrictness: model.parts[0]?.aiVerificationStrictness,
    };
  },
});
